import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { cenovaPonukaModel } from "../../../api/models/cenovaPonukaModel";
import ExportButton from "../../../exports/ExportButton";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { CenovaPonuka, CenovaPonukaSearchQuery } from "../../../ts/CP";
import { getCPStateLabel } from "../../../utils";
import Button from "../../ui/Button/Button";
import { ITEMS_PER_PAGE } from "../../ui/LazyDataGrid/common";
import { LazyDataGrid } from "../../ui/LazyDataGrid/LazyDataGrid";
import { MultiLineCell } from "../../ui/LazyDataGrid/MultiLineCell";
import "./SeznamPonuk.scss";

interface Props {
  shown: boolean;
  onlyNew?: boolean;
  query?: CenovaPonukaSearchQuery | null;
  setQuery?: (value: CenovaPonukaSearchQuery | null) => void;
  onSelectRow?: (row: CenovaPonuka) => void;
}

const SeznamPonuk = ({
  query,
  onlyNew,
  shown,
  onSelectRow,
  setQuery,
}: Props) => {
  const navigate = useNavigate();

  const { call } = useQueryWithPopupErrorHandling();
  const [data, setData] = useState<CenovaPonuka[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);

  useEffect(() => {
    const getCenovePonuky = async () => {
      setIsLoading(true);
      await call(
        () =>
          cenovaPonukaModel.getFilteredCenovePonuky({
            offset: (page * pageSize).toString(),
            limit: pageSize.toString(),
            query: query ? JSON.stringify(query) : undefined,
          }),
        "Zobrazení cenových ponuk nebolo úspešné",
        null,
        ({ data, totalCount }) => {
          setData(data);
          setTotalCount(totalCount);
        },
        undefined,
        true
      );
      setIsLoading(false);
    };

    if (shown) {
      getCenovePonuky();
    }
  }, [page, query, shown, pageSize]);

  return (
    <div
      style={{
        display: shown ? "block" : "none",
      }}
      className="cenove-ponuky-list"
    >
      <div className="cenove-ponuky-list-title">
        Kliknutím vyberiete cenovú ponuku
        {query && setQuery ? (
          <Button
            onClick={() => {
              setQuery(null);
              setPage(0);
            }}
          >
            VYMAZAT FILTR
          </Button>
        ) : null}
      </div>

      <LazyDataGrid<CenovaPonuka>
        id="cenove-ponuky"
        rows={data}
        isLoading={isLoading}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => {
          onSelectRow?.(row);
          navigate(`/cenove-ponuky/${row._id}`);
        }}
        columns={[
          {
            field: "cislo_ponuky",
            headerName: "ČÍSLO PONUKY",
            width: 115,
          },
          {
            field: "objednavatel",
            headerName: "OBJEDNÁVATEĽ",
            renderCell: ({ row }) =>
              row.objednavatel ? (
                <MultiLineCell>{row.objednavatel.nazev}</MultiLineCell>
              ) : undefined,
            width: 200,
          },
          {
            field: "kontaktna_osoba",
            headerName: "KONTAKTNÁ OSOBA",
            renderCell: ({ row }) => (
              <MultiLineCell>
                {row.kontakt
                  ? (row.kontakt.jmeno ?? "") + " " + (row.kontakt.nazev ?? "")
                  : ""}
              </MultiLineCell>
            ),
            width: 200,
          },
          {
            field: "prijimatel",
            headerName: "PRIJÍMATEĽ",
            renderCell: ({ row }) =>
              row.prijimatel
                ? (row.prijimatel.jmeno ?? "") +
                  " " +
                  (row.prijimatel.prijmeni ?? "")
                : "",
            width: 130,
          },
          {
            field: "obchodnik",
            headerName: "OBCHODNÍK",
            renderCell: ({ row }) =>
              row.kontakt && row.kontakt.obchodnik
                ? row.kontakt.obchodnik.firstName +
                  " " +
                  row.kontakt.obchodnik.lastName
                : row.kontakt?.obchodnikName ?? "",
            width: 110,
          },
          {
            field: "celkem_polozek",
            headerName: "CELKOM POLOŽIEK",
            renderCell: ({ row }) => {
              return row.polozky.length;
            },
            width: 80,
            align: "center",
          },
          {
            field: "cena_celkem",
            headerName: "CENA CELKOM",
            renderCell: ({ row }) => {
              return row.polozky
                .map((p) => p.cennikova_cena * p.mn)
                .reduce((a, b) => a + b, 0)
                .toFixed(2);
            },
            width: 110,
          },
          {
            field: "state",
            headerName: "STAV",
            renderCell: ({ row }) => getCPStateLabel(row.state),
            width: 80,
          },
          {
            field: "date",
            headerName: "DATUM",
            renderCell: ({ row }) =>
              row.date ? new Date(row.date).toLocaleDateString() : "",
            minWidth: 110,
            flex: 1,
          },
        ]}
      />

      <ExportButton
        type="cp"
        query={query ? JSON.stringify(query) : undefined}
      />
    </div>
  );
};

export default SeznamPonuk;
